import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLanguage, pick } from "@/lib/lang";
import PeonyMark from "@/components/ui-brand/PeonyMark";
import PhotoPlaceholder from "@/components/ui-brand/PhotoPlaceholder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Erlebnisraum — an in-page overlay that opens like "another room of the same house".
 * Not a new route or window — a slide-in editorial sheet with its own atmosphere.
 */
const Erlebnisraum = ({ room, onClose }) => {
  const { lang } = useLanguage();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!room) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.documentElement.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.documentElement.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [room, onClose]);

  useEffect(() => {
    // reset notify state when switching rooms
    setEmail("");
    setStatus("idle");
    setMsg("");
  }, [room?.id]);

  if (!room) return null;

  const isComingSoon = room.state === "coming_soon";

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/newsletter`, { email, language: lang });
      setStatus("success");
      setMsg(
        lang === "de"
          ? "Eingetragen. Du hörst von mir, sobald der Raum sich öffnet."
          : "Subscribed. You’ll hear from me as soon as the room opens."
      );
      setEmail("");
    } catch (err) {
      if (err?.response?.status === 409) {
        setStatus("already");
        setMsg(lang === "de" ? "Du bist bereits eingetragen." : "You’re already on the list.");
      } else {
        setStatus("error");
        setMsg(lang === "de" ? "Etwas ist schiefgelaufen." : "Something went wrong.");
      }
    }
  };

  return (
    <div
      data-testid={`erlebnisraum-${room.id}`}
      className="fixed inset-0 z-[60] flex items-stretch"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <button
        type="button"
        onClick={onClose}
        aria-label={lang === "de" ? "Raum verlassen" : "Leave the room"}
        className="absolute inset-0 bg-depth/85 backdrop-blur-md animate-bloom"
      />

      {/* Sheet */}
      <div
        className="relative ml-auto h-full w-full md:w-[88%] lg:w-[78%] xl:w-[72%] bg-canvas overflow-y-auto animate-bloom"
        style={{ animationDuration: "900ms" }}
      >
        {/* Room accent hairline */}
        <div
          className="sticky top-0 z-20 h-[2px] w-full"
          style={{ background: room.accent?.hex || "#FF7100" }}
          aria-hidden
        />

        {/* Header */}
        <div className="sticky top-[2px] z-10 bg-canvas/85 backdrop-blur-xl border-b border-warmth/40">
          <div className="px-6 md:px-12 lg:px-16 py-5 flex items-center justify-between gap-6">
            <span className="font-mono text-[0.6rem] tracking-[0.32em] uppercase text-atmosphere flex items-center gap-3">
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: room.accent?.hex || "#FF7100" }}
                aria-hidden
              />
              {lang === "de" ? "Erlebnisraum" : "Inner room"}
              <span className="text-warmth/70">N° {room.number}</span>
              {isComingSoon && (
                <span className="ml-2 px-2 py-1 border border-signal/60 text-signal text-[0.55rem]">
                  {lang === "de" ? "Im Entstehen" : "In the making"}
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={onClose}
              data-testid={`erlebnisraum-close-${room.id}`}
              className="font-mono text-[0.66rem] tracking-[0.32em] uppercase text-depth hover:text-signal transition-colors flex items-center gap-2"
            >
              {lang === "de" ? "Raum verlassen" : "Leave the room"} ×
            </button>
          </div>
        </div>

        {/* Hero */}
        <section className="relative px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12 overflow-hidden">
          <PeonyMark
            size={680}
            opacity={0.04}
            className="absolute -right-32 -top-16 pointer-events-none"
          />
          <div className="grid grid-cols-12 gap-6 lg:gap-12">
            <div className="col-span-12 lg:col-span-8">
              <p className="eyebrow">— {pick(room.eyebrow, lang)}</p>
              <h2 className="headline-hero mt-8">
                <span className="italic-serif text-atmosphere">
                  {pick(room.titleItalic, lang)}
                </span>
                {pick(room.titleRest, lang) && (
                  <>
                    <br />
                    {pick(room.titleRest, lang)}
                  </>
                )}
              </h2>
              <p className="body-lux-lg mt-10 max-w-2xl">{pick(room.lede, lang)}</p>
            </div>
            <div className="col-span-12 lg:col-span-4 lg:pt-16">
              <div
                className="pl-6 border-l"
                style={{ borderColor: room.accent?.hex || "#5C4752" }}
              >
                <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-atmosphere flex items-center gap-2">
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full"
                    style={{ background: room.accent?.hex || "#FF7100" }}
                    aria-hidden
                  />
                  {lang === "de" ? "Atmosphäre" : "Atmosphere"}
                  {room.accent?.name && (
                    <span className="text-warmth/70 ml-1">
                      · {pick(room.accent.name, lang)}
                    </span>
                  )}
                </p>
                <p className="font-display italic text-2xl text-depth mt-4">
                  {pick(room.atmosphere, lang)}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* BODY — depends on state */}
        {isComingSoon ? (
          // -------- COMING SOON --------
          <section className="px-6 md:px-12 lg:px-16 pb-20">
            <div className="grid grid-cols-12 gap-6 lg:gap-12">
              <div className="col-span-12 lg:col-span-7">
                <div className="relative aspect-[4/3] overflow-hidden bg-depth">
                  {room.image ? (
                    <img src={room.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(165deg, #28001B 0%, #5C4752 70%, #CBB7B0 130%)",
                      }}
                    />
                  )}
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(165deg, rgba(40,0,27,0.55) 0%, rgba(92,71,82,0.45) 100%)",
                    }}
                  />
                  <div className="absolute inset-0 grain pointer-events-none" />
                  <PeonyMark
                    size={520}
                    color="#FAF7F2"
                    opacity={0.08}
                    className="absolute -right-32 -bottom-32"
                  />
                  <span className="absolute top-5 left-5 w-3 h-3 border-l border-t border-canvas/60" />
                  <span className="absolute top-5 right-5 w-3 h-3 border-r border-t border-canvas/60" />
                  <span className="absolute bottom-5 left-5 w-3 h-3 border-l border-b border-canvas/60" />
                  <span className="absolute bottom-5 right-5 w-3 h-3 border-r border-b border-canvas/60" />
                  <div className="absolute inset-0 flex flex-col justify-between p-7 md:p-10 text-canvas">
                    <span className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-warmth">
                      {lang === "de" ? "Im Entstehen" : "In the making"}
                    </span>
                    <div>
                      <p className="font-display italic text-2xl md:text-3xl leading-snug max-w-md">
                        {pick(room.promise, lang)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-12 lg:col-span-5 flex flex-col justify-center">
                <p className="eyebrow">— {lang === "de" ? "Benachrichtigung" : "Notify me"}</p>
                <p className="font-display italic text-2xl md:text-3xl text-depth mt-6 leading-snug">
                  {lang === "de"
                    ? "Soll ich dir Bescheid geben, sobald sich dieser Raum öffnet?"
                    : "Shall I let you know when this room opens?"}
                </p>
                <form
                  onSubmit={handleNotify}
                  data-testid={`erlebnisraum-${room.id}-notify-form`}
                  className="mt-10 border-b border-depth/30 flex items-end gap-4 pb-2 focus-within:border-depth transition-colors"
                >
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={lang === "de" ? "deine@adresse.de" : "you@somewhere.com"}
                    data-testid={`erlebnisraum-${room.id}-notify-input`}
                    className="bg-transparent outline-none border-none w-full text-depth placeholder:text-warmth font-light text-lg py-2"
                  />
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid={`erlebnisraum-${room.id}-notify-submit`}
                    className="font-mono text-[0.7rem] tracking-[0.32em] uppercase text-depth hover:text-signal transition-colors py-2 disabled:opacity-50"
                  >
                    {status === "sending" ? "…" : lang === "de" ? "Eintragen" : "Subscribe"} ↗
                  </button>
                </form>
                {status !== "idle" && status !== "sending" && (
                  <p
                    data-testid={`erlebnisraum-${room.id}-notify-status`}
                    className={`mt-5 text-sm font-light ${status === "error" ? "text-signal" : "text-atmosphere"}`}
                  >
                    {msg}
                  </p>
                )}
              </div>
            </div>
          </section>
        ) : (
          // -------- ACTIVE WORKS TETRIS --------
          <section className="px-6 md:px-12 lg:px-16 pb-16 md:pb-24">
            <div className="mb-8 flex items-baseline justify-between">
              <p className="eyebrow">
                — {lang === "de" ? "Werke" : "Works"}
              </p>
              <p className="font-mono text-[0.6rem] tracking-[0.32em] uppercase text-atmosphere/70">
                {(room.works || []).length} {lang === "de" ? "Plätze" : "slots"}
              </p>
            </div>
            <div className="grid grid-cols-12 gap-3 md:gap-4">
              {(room.works || []).map((w, i) => {
                // Editorial layout — first two items get hero / portrait pairing,
                // subsequent items fall into the magazine rhythm. Per-work override possible via `w.colspan`.
                const fallback = [
                  "col-span-12 md:col-span-8",  // hero (landscape)
                  "col-span-12 md:col-span-4",  // portrait next to it
                  "col-span-6 md:col-span-3",
                  "col-span-6 md:col-span-3",
                  "col-span-12 md:col-span-6",
                  "col-span-6 md:col-span-3",
                  "col-span-6 md:col-span-3",
                ][i % 7];
                const colspan = w.colspan || fallback;
                const caption = w.caption
                  ? typeof w.caption === "string"
                    ? w.caption
                    : pick(w.caption, lang)
                  : undefined;
                return (
                  <div key={i} className={colspan}>
                    <PhotoPlaceholder
                      testId={`erlebnisraum-${room.id}-work-${i}`}
                      src={w.src}
                      alt={caption || ""}
                      label={lang === "de" ? "Foto folgt" : "Coming"}
                      caption={caption}
                      aspect={w.aspect || "4/5"}
                      tone={i % 2 === 0 ? "warm" : "depth"}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Manifesto/Closing */}
        <section className="relative bg-soft_canvas border-t border-warmth/40 px-6 md:px-12 lg:px-16 py-20 md:py-28 overflow-hidden">
          <PeonyMark
            size={520}
            opacity={0.05}
            className="absolute -left-32 -bottom-20 pointer-events-none"
          />
          <div className="grid grid-cols-12 gap-6 lg:gap-12 relative">
            <div className="col-span-12 lg:col-span-9">
              <p className="eyebrow">— {lang === "de" ? "Coda" : "Coda"}</p>
              <p className="font-display italic text-[clamp(1.8rem,3.4vw,3rem)] leading-[1.1] mt-6 text-depth max-w-3xl">
                {pick(room.closing, lang)}
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Erlebnisraum;
